import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';

import Icon from '@hydra/common/components/Icon';
import Translate from '@hydra/common/components/i18n';
import RatingIcons from '@hydra/common/components/RatingIcons';
import { getLightColor } from '@hydra/utils/colors';
import { useReviewContext } from '@hydra/common/context/review';
import { usePdfTemplateContext } from '@hydra/common/context/pdfTemplate';

import { getStatus } from '@hydra/utils/criterium';

const useStyles = makeStyles(theme => ({
  head: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    marginBottom: theme.spacing(1)
  },
  importanceIcons: {
    display: 'inline-block',
    marginRight: theme.spacing(2)
  },
  solvabilityIcons: {
    display: 'inline-block'
  },
  statusIcon: {
    display: 'flex',
    marginRight: theme.spacing(0.75)
  },
  titleModern: {
    display: 'flex',
    lineHeight: '1.5rem',
    fontSize: theme.typography.fontSizeLarge
  },
  titleClassic: {
    display: 'flex',
    lineHeight: 1.6,
    fontSize: theme.typography.fontSizeMedium
  },
  icons: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap'
  },
  iconsModern: {
    marginTop: theme.spacing(1),
    marginLeft: 0
  },
  iconsClassic: {
    marginLeft: theme.spacing(3)
  },
  tooltip: {
    fontSize: theme.typography.fontSizeSmall,
    lineHeight: 1.25
  }
}));

export const CriteriumHead = ({ criterium, iconsFillColor }) => {
  const classes = useStyles();
  const theme = useTheme();
  const { isPdf } = useReviewContext();
  const { pdfTheme } = usePdfTemplateContext();
  const { type, importance, solvability, score } = criterium;

  let emptyColor;
  if (isPdf) {
    emptyColor = getLightColor(iconsFillColor, 0.2);
  } else {
    emptyColor = theme.palette.secondary.light;
  }

  const isModern = isPdf && pdfTheme === 'modern';

  const status = getStatus(score);
  const iconsClass = classnames(
    classes.icons,
    isModern ? classes.iconsModern : classes.iconsClassic
  );
  const iconSize = isModern ? 'base' : 'sm';
  const titleClass = isModern
    ? classes.titleModern
    : classes.titleClassic;
  const ratingIconSize = isModern ? 'sm' : 'xs';

  return (
    <div className='mex-criterium-head'>
      <Typography variant='h3' className={classes.head}>
        <Tooltip title={<Translate content={`${status.text}`} className={classes.tooltip} />} placement='top'>
          <span className={classnames(classes.statusIcon, `nr-review-${type}-score-icon`)}>
            <Icon type={status.type} size={iconSize} />
          </span>
        </Tooltip>
        <span className={titleClass}>
          <Translate content={type} />
        </span>
      </Typography>
      <div className={iconsClass}>
        {
          importance > 0 &&
            <Tooltip
              title={<Translate content={`criterion_importance${importance}`} className={classes.tooltip} />}
              placement='top'
            >
              <div className={classnames(classes.importanceIcons, `nr-review-${type}-impact-icons`)}>
                <RatingIcons
                  rating={importance}
                  type='importance'
                  fillColor={iconsFillColor}
                  emptyColor={emptyColor}
                  size={ratingIconSize}
                />
              </div>
            </Tooltip>
        }
        {
          solvability > 0 &&
            <Tooltip title={<Translate content={`criterion_solvability${solvability}`} className={classes.tooltip} />} placement='top'>
              <div className={classnames(classes.solvabilityIcons, `nr-review-${type}-effort-icons`)}>
                <RatingIcons
                  rating={solvability}
                  type='solvability'
                  fillColor={iconsFillColor}
                  emptyColor={emptyColor}
                  size={ratingIconSize}
                />
              </div>
            </Tooltip>
        }
      </div>
    </div>
  );
};

CriteriumHead.propTypes = {
  criterium: PropTypes.object.isRequired
};

export default CriteriumHead;
